/**
 * The workbench's screen. Two ways to it, the same to the person:
 *
 *  - over the mesh (Enterprise, a machine on the mesh): the API streams the workbench's MJPEG mirror from its control
 *    service through the gateway, and input goes as one signed POST per event;
 *  - over the relay (Mojo Up AI Cloud always, and an Enterprise machine off the mesh): a `mirror.subscribe` channel
 *    (mirrorChannel.ts) carries frames in parts and input on the same channel.
 *
 * Viewing needs ui:view. Driving the window (clicks, scrolling, typing) is a separate step: ui:control, granted here
 * with one press, a recent sign-in, plus the setting on the workbench that lets a remote client control it, which its
 * own person turns on. While someone drives, the workbench shows what they do.
 *
 * Full screen gives the screen the whole display (the browser's own full screen; Esc leaves it), with a small bar
 * at the top for control and leaving. A phone that cannot put part of a page full screen does not offer it.
 */

import { mirrorCloseKind, type MirrorCloseKind, type MirrorInput } from '@aioe/shared';
import { ArrowsExpand, Display } from '@gravity-ui/icons';
import { Tooltip } from '@heroui/react';
import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent, type WheelEvent } from 'react';

import { API_URL, ApiError, currentToken, routes, SIGN_IN_AGAIN, withOrganisation } from '../../api/client.ts';
import { useAuth } from '../../auth/AuthProvider.tsx';
import { Button, Empty, ErrorNote, StatusMark } from '../../components/ui.tsx';
import { isCloud } from '../../edition.ts';
import { openMirror, type MirrorChannel } from '../mirrorChannel.ts';
import type { Workbench } from '../useWorkbench.ts';
import { RemoteView } from './RemoteView.tsx';

function indexOf(buf: Uint8Array, a: number, b: number, from: number): number {
  for (let i = from; i < buf.length - 1; i++) if (buf[i] === a && buf[i + 1] === b) return i;
  return -1;
}

const MODIFIERS = (e: { shiftKey: boolean; ctrlKey: boolean; altKey: boolean; metaKey: boolean }) =>
  [e.shiftKey && 'shift', e.ctrlKey && 'control', e.altKey && 'alt', e.metaKey && 'meta'].filter((m): m is 'shift' | 'control' | 'alt' | 'meta' => Boolean(m));

/** The widest frame the console asks for over the relay; the platform may hold it lower. */
const RELAY_MAX_WIDTH = 1600;

type State = 'connecting' | 'live' | 'not-granted' | 'off' | 'unavailable' | 'no-screen' | 'window-closed' | 'kill' | 'policy' | 'needs-update' | 'limit' | 'teams' | 'offline';

/** A relay close as the view's state; undefined when it is about control, which only ends control. */
function stateOf(kind: MirrorCloseKind): State | undefined {
  switch (kind) {
    case 'not_granted':
      return 'not-granted';
    case 'needs_update':
      return 'needs-update';
    case 'limit':
      return 'limit';
    case 'teams_required':
      return 'teams';
    case 'offline':
      return 'offline';
    case 'off':
      return 'off';
    case 'kill_switch':
      return 'kill';
    case 'policy_off':
      return 'policy';
    case 'no_screen':
      return 'no-screen';
    case 'window_closed':
      return 'window-closed';
    case 'control_not_granted':
    case 'control_withdrawn':
    case 'sign_in_again':
      return undefined;
    default:
      return 'unavailable';
  }
}

const CONTROL_OFF = 'The workbench\'s person has remote control of the window turned off. In the app, Settings → Security → "Allow clicking and typing" turns it on; the window shows a warning while it is possible.';

export function ScreenView({ w }: { w: Workbench }) {
  const [frame, setFrame] = useState<string>();
  const [state, setState] = useState<State>('connecting');
  const [limitWords, setLimitWords] = useState<string>();
  /** The mesh first on Enterprise; the relay on Cloud, and wherever the mesh does not reach the machine. */
  const [path, setPath] = useState<'mesh' | 'relay'>(isCloud() ? 'relay' : 'mesh');
  const [attempt, setAttempt] = useState(0);
  const [allowing, setAllowing] = useState(false);
  const [controlling, setControlling] = useState(false);
  const [controlError, setControlError] = useState<string>();
  const last = useRef<string | undefined>(undefined);
  const img = useRef<HTMLImageElement>(null);
  const stage = useRef<HTMLDivElement>(null);
  /** What goes full screen: the bar and the stage together, so keys still reach the stage while in control. */
  const screen = useRef<HTMLDivElement>(null);
  const [fullScreen, setFullScreen] = useState(false);
  const canFullScreen = typeof document !== 'undefined' && document.fullscreenEnabled;
  const lastMove = useRef(0);
  const channel = useRef<MirrorChannel | undefined>(undefined);
  /** The relay's credit for the frame on screen, granted back once the browser has drawn it. */
  const drawn = useRef<(() => void) | undefined>(undefined);
  /** A step-up sign-in is under way: input waits rather than piling up refusals. */
  const steppingUp = useRef(false);
  const { signInAgain } = useAuth();
  // The relay channel reads it through a ref: the function changes while a step-up is shown, which must not reopen it.
  const signInAgainRef = useRef(signInAgain);
  signInAgainRef.current = signInAgain;

  // Keys go to the stage while in control, so it takes focus when control starts.
  useEffect(() => {
    if (controlling) stage.current?.focus();
  }, [controlling, fullScreen]);

  // Esc, or the browser's own control, leaves full screen: follow it.
  useEffect(() => {
    const follow = () => setFullScreen(!!screen.current && document.fullscreenElement === screen.current);
    document.addEventListener('fullscreenchange', follow);
    return () => document.removeEventListener('fullscreenchange', follow);
  }, []);

  function toggleFullScreen() {
    if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
    else void screen.current?.requestFullscreen().catch(() => undefined);
  }

  // Over the mesh: the MJPEG stream, each complete JPEG drawn as it arrives.
  useEffect(() => {
    if (path !== 'mesh') return;
    const ac = new AbortController();
    (async () => {
      const token = await currentToken();
      const res = await fetch(`${API_URL}/relay/v1/devices/${encodeURIComponent(w.deviceId)}/mirror/stream`, { headers: withOrganisation(token ? { authorization: `Bearer ${token}` } : {}), signal: ac.signal }).catch(() => undefined);
      if (ac.signal.aborted) return;
      if (!res || !res.body) return setState('unavailable');
      // Not on the mesh (or the edition does not offer it): the relay carries the screen instead.
      if (res.status === 409 || res.status === 404) return setPath('relay');
      if (res.status === 403) return setState('not-granted');
      if (res.status === 429) {
        setLimitWords(((await res.json().catch(() => undefined)) as { message?: string } | undefined)?.message);
        return setState('limit');
      }
      if (!res.ok) return setState('unavailable');
      setState('live');
      const reader = res.body.getReader();
      let buf = new Uint8Array(0);
      for (;;) {
        const { value, done } = await reader.read().catch(() => ({ value: undefined, done: true }));
        if (done || !value) break;
        const merged = new Uint8Array(buf.length + value.length);
        merged.set(buf);
        merged.set(value, buf.length);
        buf = merged;
        // Take every complete JPEG (FFD8 … FFD9) and show the newest.
        for (;;) {
          const start = indexOf(buf, 0xff, 0xd8, 0);
          if (start < 0) break;
          const end = indexOf(buf, 0xff, 0xd9, start + 2);
          if (end < 0) break;
          const url = URL.createObjectURL(new Blob([buf.slice(start, end + 2)], { type: 'image/jpeg' }));
          if (last.current) URL.revokeObjectURL(last.current);
          last.current = url;
          setFrame(url);
          buf = buf.slice(end + 2);
        }
        if (buf.length > 8 << 20) buf = new Uint8Array(0);
      }
      if (!ac.signal.aborted) setState('unavailable');
    })();
    return () => {
      ac.abort();
      if (last.current) URL.revokeObjectURL(last.current);
      last.current = undefined;
    };
  }, [w.deviceId, attempt, path]);

  // Over the relay: one channel, reopened with input when control is taken.
  useEffect(() => {
    if (path !== 'relay') return;
    let live = true;
    const relayControl = controlling;
    void (async () => {
      let ch: MirrorChannel;
      try {
        ch = await openMirror(w.deviceId, { control: relayControl, maxWidth: RELAY_MAX_WIDTH }, {
          onOpened: (opened) => {
            if (!live) return;
            setState('live');
            if (relayControl && opened && !opened.control) {
              setControlling(false);
              setControlError(CONTROL_OFF);
            }
          },
          onFrame: (f, done) => {
            if (!live) return done();
            // The frame before never loaded: its credit goes back now rather than never.
            drawn.current?.();
            drawn.current = done;
            setFrame(`data:image/jpeg;base64,${f.b64}`);
          },
          onClosed: ({ code, reason }) => {
            if (!live) return;
            const kind = mirrorCloseKind(code, reason, relayControl);
            if (kind === 'limit') setLimitWords(reason?.replace(/^[a-z_]+:\s*/, ''));
            if (relayControl && (kind === 'control_withdrawn' || kind === 'control_not_granted' || kind === 'sign_in_again')) {
              // Back to watching (the channel reopens without input), saying why.
              setControlling(false);
              setControlError(
                kind === 'control_withdrawn'
                  ? "The workbench's person turned clicking and typing off. You can still watch."
                  : kind === 'sign_in_again'
                    ? 'Control needs a recent sign-in. Take control again when you are ready to sign in.'
                    : "Screen control is not allowed for this workbench. Its owner or an administrator can turn it on in Settings.",
              );
              return;
            }
            setFrame(undefined);
            setState(stateOf(kind) ?? 'unavailable');
          },
        });
      } catch (e) {
        if (!live) return;
        // Step-up: the sign-in is too old to take control. Ask for a fresh one, then open again.
        if (relayControl && e instanceof ApiError && e.code === SIGN_IN_AGAIN) {
          const message = typeof (e.body as { message?: unknown })?.message === 'string' ? (e.body as { message: string }).message : 'Sign in again to take control of this workbench.';
          const stepUp = signInAgainRef.current;
          if (stepUp) {
            steppingUp.current = true;
            await stepUp(message)
              .then(() => live && setAttempt((n) => n + 1))
              .catch(() => {
                if (!live) return;
                setControlling(false);
                setControlError('Control needs a recent sign-in. Take control again when you are ready to sign in.');
              })
              .finally(() => (steppingUp.current = false));
          } else {
            setControlling(false);
            setControlError(message);
          }
          return;
        }
        setState(e instanceof ApiError && e.code === 'teams_required' ? 'teams' : e instanceof ApiError && e.status === 404 ? 'unavailable' : 'offline');
        return;
      }
      if (!live) return ch.close();
      channel.current = ch;
    })();
    return () => {
      live = false;
      channel.current?.close();
      channel.current = undefined;
      drawn.current = undefined;
    };
  }, [w.deviceId, attempt, path, controlling]);

  /** Grant a ui scope on top of what the workbench already holds. */
  async function grant(scope: 'ui:view' | 'ui:control') {
    const { scopes } = await routes.deviceScopes(w.deviceId);
    if (!scopes.includes(scope)) await routes.setDeviceScopes(w.deviceId, [...scopes, scope]);
  }

  const retry = () => {
    setState('connecting');
    setAttempt((n) => n + 1);
  };

  async function allowViewing() {
    setAllowing(true);
    try {
      await grant('ui:view');
      retry();
    } finally {
      setAllowing(false);
    }
  }

  async function takeControl() {
    setAllowing(true);
    setControlError(undefined);
    try {
      await grant('ui:control');
      setControlling(true);
    } catch (e) {
      setControlError((e as Error).message);
    } finally {
      setAllowing(false);
    }
  }

  /**
   * Release control. The workbench's title-bar badge follows the stream's lifetime (a stream
   * whose signed input arrived counts as a controller), so the stream is reopened for viewing
   * only and the badge drops back to "watched". Over the relay the channel is reopened without input.
   */
  function handBack() {
    setControlling(false);
    if (path === 'mesh') setAttempt((n) => n + 1);
  }

  /** Over the mesh: one signed POST; the workbench's own refusal ends control mode with its reason. */
  function sendMesh(event: MirrorInput) {
    void routes.mirrorInput(w.deviceId, event).catch((e: unknown) => {
      // Step-up: the sign-in is too old to take control. Ask for a fresh one, then send the event again.
      if (e instanceof ApiError && e.code === SIGN_IN_AGAIN) {
        const message = typeof (e.body as { message?: unknown })?.message === 'string' ? (e.body as { message: string }).message : 'Sign in again to take control of this workbench.';
        if (!signInAgain) {
          setControlling(false);
          setControlError(message);
          return;
        }
        steppingUp.current = true;
        void signInAgain(message)
          .then(() => {
            steppingUp.current = false;
            send(event);
          })
          .catch(() => {
            steppingUp.current = false;
            setControlling(false);
            setControlError('Control needs a recent sign-in. Take control again when you are ready to sign in.');
          });
        return;
      }
      setControlling(false);
      if (e instanceof ApiError && e.status === 403) setControlError(CONTROL_OFF);
      else if (e instanceof ApiError && e.status === 404) setControlError("This workbench does not accept input over the mesh: its version serves the screen but not clicks and typing yet, or it has no window to drive. Viewing still works.");
      else if (e instanceof ApiError && e.status === 409) setControlError('Driving the screen needs the workbench on the mesh.');
      else if (e instanceof ApiError && e.status === 503) {
        // The workbench refused: control is off there, or the window is gone. Its own words, plus where the switch is.
        const reason = typeof (e.body as { error?: unknown })?.error === 'string' ? (e.body as { error: string }).error : 'the window refused';
        setControlError(`${reason[0]!.toUpperCase()}${reason.slice(1)}. In the app, Settings → Security → "Allow clicking and typing" is the switch.`);
      } else setControlError((e as Error).message);
    });
  }

  function send(event: MirrorInput) {
    if (steppingUp.current) return;
    if (path === 'mesh') return sendMesh(event);
    // The channel takes the mesh's input language, in the drawn frame's coordinates.
    channel.current?.input(event);
  }

  /** A pointer position on the image, in the frame's own pixels (the workbench scales to its window). Full screen letterboxes the frame (object-contain), so the margins are taken off. */
  function at(e: { clientX: number; clientY: number }): { x: number; y: number } | undefined {
    const el = img.current;
    if (!el || !el.naturalWidth || !el.naturalHeight) return undefined;
    const r = el.getBoundingClientRect();
    const scale = Math.min(r.width / el.naturalWidth, r.height / el.naturalHeight);
    if (!scale) return undefined;
    const x = (e.clientX - r.left - (r.width - el.naturalWidth * scale) / 2) / scale;
    const y = (e.clientY - r.top - (r.height - el.naturalHeight * scale) / 2) / scale;
    // Held to the frame's edge: a release in the margin still lets go of the button.
    return { x: Math.round(Math.min(Math.max(x, 0), el.naturalWidth)), y: Math.round(Math.min(Math.max(y, 0), el.naturalHeight)) };
  }

  const BUTTONS = ['left', 'middle', 'right'] as const;
  const onPointer = (action: 'down' | 'up' | 'move') => (e: PointerEvent<HTMLImageElement>) => {
    if (!controlling) return;
    if (action === 'move') {
      const now = performance.now();
      if (now - lastMove.current < 33) return; // about thirty a second is plenty
      lastMove.current = now;
    } else e.preventDefault();
    const p = at(e);
    if (p) send({ type: 'mouse', action, ...p, button: BUTTONS[e.button] ?? 'left', ...(action === 'down' ? { clickCount: Math.min(3, Math.max(1, e.detail)) } : {}) });
  };
  const onWheel = (e: WheelEvent<HTMLImageElement>) => {
    if (!controlling) return;
    const p = at(e);
    if (p) send({ type: 'wheel', ...p, deltaX: e.deltaX, deltaY: e.deltaY });
  };
  const onKey = (action: 'down' | 'up') => (e: KeyboardEvent<HTMLDivElement>) => {
    if (!controlling) return;
    e.preventDefault();
    const modifiers = MODIFIERS(e);
    // A printable key without control/alt/meta is typed as a character on the way down.
    if (action === 'down' && e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) {
      send({ type: 'key', action: 'char', key: e.key });
      return;
    }
    if (e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) return;
    send({ type: 'key', action, key: e.key, modifiers: modifiers.length ? modifiers : undefined });
  };

  const tryAgain = (
    <Button size="sm" variant="secondary" onPress={retry}>
      Try again
    </Button>
  );
  if (state === 'not-granted')
    return (
      <RemoteView narrow>
        <Empty
          tone="blue"
          icon={<Display />}
          title="Screen viewing is not allowed"
          body="This workbench was enrolled without screen viewing. Its owner or an administrator can allow it here or in Settings; the workbench's person still decides in the app whether to share."
          action={
            <Button size="sm" isPending={allowing} onPress={() => void allowViewing()}>
              Allow screen viewing
            </Button>
          }
        />
      </RemoteView>
    );
  if (state === 'needs-update')
    return (
      <RemoteView narrow>
        <Empty tone="blue" icon={<Display />} title="This workbench needs an update to share its screen here" body="Its version shares the screen only over a private network. Once AI Workbench is updated on that computer, its screen can be watched here." action={tryAgain} />
      </RemoteView>
    );
  if (state === 'limit')
    return (
      <RemoteView narrow>
        <Empty tone="blue" icon={<Display />} title="Too many screens open" body={limitWords ?? 'Your organisation is watching as many screens as it may at once. Close one, then try again.'} action={tryAgain} />
      </RemoteView>
    );
  if (state === 'teams')
    return (
      <RemoteView narrow>
        <Empty tone="blue" icon={<Display />} title="Screens are part of Teams" body="Watching a workbench's screen through AI Cloud comes with Teams. Subscribe on your team's Billing page." />
      </RemoteView>
    );
  if (state === 'offline' && !frame)
    return (
      <RemoteView narrow>
        <Empty tone="blue" icon={<Display />} title="The workbench is not connected" body="Its screen can be watched once the workbench is running and connected to your organisation." action={tryAgain} />
      </RemoteView>
    );
  if (state === 'off' && !frame)
    return (
      <RemoteView narrow>
        <Empty tone="blue" icon={<Display />} title="The screen is not being shared" body="The workbench shares its screen only when its person has turned screen mirroring on in the app; it shows a bar while anyone is watching." action={tryAgain} />
      </RemoteView>
    );
  if (state === 'unavailable' && !frame)
    return (
      <RemoteView narrow>
        <Empty tone="blue" icon={<Display />} title="There is no window to share" body="A node has no screen to share, and a workbench has none while its window is closed." action={tryAgain} />
      </RemoteView>
    );
  if (state === 'no-screen' && !frame)
    return (
      <RemoteView narrow>
        <Empty tone="blue" icon={<Display />} title="This node has no screen" body="A node runs without a window, so there is nothing to watch." />
      </RemoteView>
    );
  if (state === 'window-closed' && !frame)
    return (
      <RemoteView narrow>
        <Empty tone="blue" icon={<Display />} title="The workbench's window is closed right now" body="Open it on that computer, then try again." action={tryAgain} />
      </RemoteView>
    );
  if (state === 'policy' && !frame)
    return (
      <RemoteView narrow>
        <Empty tone="blue" icon={<Display />} title="Your organisation's policy doesn't allow remote control there" body="The policy this workbench follows keeps remote control off, the screen included." />
      </RemoteView>
    );
  if (state === 'kill' && !frame)
    return (
      <RemoteView narrow>
        <Empty tone="blue" icon={<Display />} title="Remote control was switched off on that workbench" body="The workbench's person has remote control switched off on that computer, so nothing reaches it, the screen included." action={tryAgain} />
      </RemoteView>
    );
  const controlButtons = controlling ? (
    <>
      <StatusMark state="working" label="You are in control" />
      <Button size="sm" variant="secondary" onPress={handBack}>
        Hand back
      </Button>
    </>
  ) : (
    <>
      <StatusMark state="connected" label="Viewing" />
      <Button size="sm" variant="secondary" isPending={allowing} isDisabled={!frame} onPress={() => void takeControl()}>
        Take control
      </Button>
    </>
  );
  return (
    <RemoteView
      fill
      actions={
        <>
          {controlButtons}
          {canFullScreen && (
            <Tooltip delay={300}>
              <Tooltip.Trigger>
                <Button size="sm" variant="ghost" isIconOnly aria-label="Full screen" isDisabled={!frame} onPress={toggleFullScreen}>
                  <ArrowsExpand className="size-4" />
                </Button>
              </Tooltip.Trigger>
              <Tooltip.Content>Full screen</Tooltip.Content>
            </Tooltip>
          )}
        </>
      }
    >
      {(controlling || controlError) && (
        <div className="flex shrink-0 flex-col gap-2 px-5 pb-3 text-sm">
          {controlling && <p className="m-0 text-muted">Click, scroll and type on the screen; the workbench shows what you do.</p>}
          <ErrorNote error={controlError} />
        </div>
      )}
      <div ref={screen} className="relative flex min-h-0 flex-1 flex-col bg-surface-secondary">
        {fullScreen && (
          <div className="absolute top-3 left-1/2 z-10 flex -translate-x-1/2 items-center gap-3 rounded-full border border-separator bg-overlay/80 px-4 py-1.5 shadow-overlay backdrop-blur">
            {controlButtons}
            <Button size="sm" variant="ghost" onPress={toggleFullScreen}>
              Exit full screen
            </Button>
          </div>
        )}
        {fullScreen && controlError && (
          <div className="absolute top-16 left-1/2 z-10 w-[min(36rem,90%)] -translate-x-1/2">
            <ErrorNote error={controlError} />
          </div>
        )}
        <div
          className={`flex min-h-0 flex-1 items-center justify-center outline-none ${fullScreen ? '' : 'p-4'} ${controlling ? 'ring-2 ring-inset ring-accent' : ''}`}
          tabIndex={controlling ? 0 : -1}
          onKeyDown={onKey('down')}
          onKeyUp={onKey('up')}
          ref={stage}
        >
          {frame ? (
            <img
              ref={img}
              src={frame}
              alt="The workbench's screen"
              draggable={false}
              className={`${fullScreen ? 'h-full w-full object-contain' : 'max-h-full max-w-full rounded-lg shadow-2xl'} ${controlling ? 'cursor-crosshair select-none' : ''}`}
              onLoad={() => {
                drawn.current?.();
                drawn.current = undefined;
              }}
              onPointerDown={onPointer('down')}
              onPointerUp={onPointer('up')}
              onPointerMove={onPointer('move')}
              onWheel={onWheel}
              onContextMenu={(e) => controlling && e.preventDefault()}
            />
          ) : (
            <span className="text-sm text-muted">Connecting…</span>
          )}
        </div>
      </div>
    </RemoteView>
  );
}
